// src/utils/lessonCloseout.js
//
// Pass 84 — ONE-MINUTE POST-LESSON CLOSEOUT (pure domain; no Firestore, no React).
//
// A closeout is a FAST human-evidence writer ONTO the canonical `lessonInstance` (Pass 66) — never a new
// collection, never a second lesson identity, never a second delivery predicate. It reuses the Quick Lesson
// Log transform (`applyQuickLessonLogToLessonInstance`) and the idempotent writer (`saveQuickLessonLog`).
//
// HARD INVARIANTS (Pass 84 §2):
//   • BOOKING IS NOT DELIVERY. Elapsed time / `booking.status` can NEVER complete a lesson. A closeout only
//     writes delivery-bearing content when the instructor takes an EXPLICIT occurrence action (`occurred===true`)
//     AND supplies real human evidence. `isDeliveryBearing` (lessonHandoff.js) is unchanged — this module adds
//     NO new delivery flag; the explicit acknowledgement is the gate that guards the write.
//   • HUMAN EVIDENCE IS PRIMARY. The instructor's typed / dictated note is the canonical source material and is
//     stored in the existing human fields (`lessonOutcome.instructorSummary` + the stage note). AI enrichment is
//     DERIVED, optional and non-blocking — it is NOT part of the fast path and never overwrites the human note.
//   • NO IDENTITY FABRICATION. A closeout carries only canonical ids already on the booking/lesson; it never
//     invents a studentId/clientId from a name (delegated to the existing writers).
//
// This module is a pure decision layer. The orchestration writer is `lessonCloseoutWriter.js`.

import { QUICK_LESSON_OUTCOME_OPTIONS } from './quickLessonLog';
import { isDeliveryBearing } from './lessonHandoff';

const str = (v) => String(v == null ? '' : v).trim();

// Provenance markers — the closeout stores HUMAN evidence; any AI-organised text is a separate, derived artifact
// and must be labelled/segregated by its consumer (the existing `video.aiReview` model). Human is never relabelled.
export const CLOSEOUT_EVIDENCE_PROVENANCE = Object.freeze({ HUMAN: 'human_closeout', AI_DERIVED: 'ai_derived' });

// The outcome vocabulary is the existing Quick Lesson Log set (no new coaching scale).
export const CLOSEOUT_OUTCOME_OPTIONS = QUICK_LESSON_OUTCOME_OPTIONS;

export const CLOSEOUT_NOT_ACKNOWLEDGED = 'Confirm the lesson took place before completing the closeout.';
export const CLOSEOUT_NOTE_REQUIRED = 'Add a note (typed or dictated) before completing the closeout.';

// Pass 84.1 — the required human evidence for a COMPLETED closeout is the coaching NOTE (typed or browser-dictated).
// A non-neutral outcome or a target skill is OPTIONAL enrichment and does NOT substitute for the note. This keeps the
// completion rule consistent with `deriveCloseoutState` (which defines COMPLETE by the presence of a human note), so an
// outcome-only save can never produce the "saved but REPORT_MISSING" contradiction.
export function closeoutHasHumanEvidence({ note = '' } = {}) {
  return !!str(note);
}

// The explicit-occurrence + note gate. `occurred` MUST be an explicit boolean true (a user action) — a truthy string /
// 1 / object does NOT satisfy it, so a form default or an implicit side effect can never pass. A completed closeout
// additionally REQUIRES a non-empty human note (outcome/target skill alone are never sufficient).
export function evaluateCloseout({ occurred = false, note = '' } = {}) {
  if (occurred !== true) return { ok: false, reason: 'not_acknowledged' };
  if (!str(note)) return { ok: false, reason: 'note_required' };
  return { ok: true, reason: '' };
}

export function assertCloseoutReady(input = {}) {
  const r = evaluateCloseout(input);
  if (!r.ok) {
    const e = new Error(r.reason === 'not_acknowledged' ? CLOSEOUT_NOT_ACKNOWLEDGED : CLOSEOUT_NOTE_REQUIRED);
    e.code = r.reason === 'not_acknowledged' ? 'CLOSEOUT_NOT_ACKNOWLEDGED' : 'CLOSEOUT_NOTE_REQUIRED';
    throw e;
  }
  return true;
}

// Build the `delivery` object consumed by `saveQuickLessonLog` in CLOSEOUT mode. The human note maps to `quickNote`
// (stored verbatim in the canonical `lessonOutcome.instructorSummary`). It carries NO stage value — the closeout is
// additive and never rebuilds stage progress. `outcomeChoice` is passed through ONLY when the instructor explicitly
// chose one (`explicitOutcome`); otherwise the existing `lessonOutcome.status`/`skillConfidence` are preserved.
export function buildCloseoutDelivery({
  note = '', outcomeChoice = null, explicitOutcome = false, recommendedNextSkillId = '',
} = {}) {
  const chosen = explicitOutcome && CLOSEOUT_OUTCOME_OPTIONS.includes(outcomeChoice) ? outcomeChoice : null;
  return {
    quickNote: str(note),
    outcomeChoice: chosen,        // null unless explicitly chosen AND valid
    explicitOutcome: !!chosen,    // the writer applies an outcome ONLY when this is true
    recommendedNextSkillId: str(recommendedNextSkillId),
  };
}

// ── Outstanding-report state (§14) — DELIVERY-evidence driven, NEVER date-driven ────────────────────────────
// A closeout can only be "missing" once a lesson is genuinely DELIVERED. A mere past booking with no lesson, or
// a started-but-empty draft, is NOT a missing report (it is "delivery unconfirmed") — so old source bookings that
// were never canonically delivered can never raise a false-critical "missing report" alert.
export const CLOSEOUT_STATE = Object.freeze({
  NONE: 'none',                            // no lessonInstance yet — nothing delivered
  DELIVERY_UNCONFIRMED: 'delivery_unconfirmed', // a lesson exists but is not delivery-bearing (empty/started draft)
  REPORT_MISSING: 'report_missing',        // DELIVERED, but the human coaching note is absent
  COMPLETE: 'complete',                     // delivered + a human coaching note is present
});

function lessonHasHumanNote(lesson = {}) {
  const summary = str(lesson?.lessonOutcome?.instructorSummary);
  const stages = Array.isArray(lesson?.stages) ? lesson.stages : [];
  const stageNote = stages.some((s) => str(s?.instructorNotes));
  return !!(summary || stageNote);
}

// Derive the closeout state of a lesson. `lesson` may be null (no lessonInstance created yet → NONE). Delivery is
// the canonical `isDeliveryBearing` predicate ONLY — never `booking.status`, never elapsed time.
export function deriveCloseoutState(lesson) {
  if (!lesson) return CLOSEOUT_STATE.NONE;
  if (!isDeliveryBearing(lesson)) return CLOSEOUT_STATE.DELIVERY_UNCONFIRMED;
  return lessonHasHumanNote(lesson) ? CLOSEOUT_STATE.COMPLETE : CLOSEOUT_STATE.REPORT_MISSING;
}

// Outstanding = DELIVERED but the human coaching note is missing. This is the ONLY condition that raises a
// "needs closeout" signal — never a past date, never booking.status, never a lesson that was never delivered.
export function isCloseoutOutstanding(lesson) {
  return deriveCloseoutState(lesson) === CLOSEOUT_STATE.REPORT_MISSING;
}

// Convenience for UI: a short, honest label for the current closeout state of a (booking, lesson) pair.
export function closeoutStateLabel(lesson) {
  switch (deriveCloseoutState(lesson)) {
    case CLOSEOUT_STATE.COMPLETE: return 'Closeout complete';
    case CLOSEOUT_STATE.REPORT_MISSING: return 'Delivered — closeout needed';
    case CLOSEOUT_STATE.DELIVERY_UNCONFIRMED: return 'Not yet closed out';
    default: return '';
  }
}
